/**
 * Canonical color palette for MARS / portfolio / forecasts charts.
 *
 * Unifies the duplicated palettes that currently live in:
 *   - frontend/src/features/mars-live/color.ts        (hexToRgba only)
 *   - frontend/src/features/mars-forecasts/utils.ts   (hexToRgba)
 *   - frontend/src/features/mars-portfolio/chartUtils.ts
 *     (ASSET_COLORS, FULCRUM_COLORS, applyOpacity)
 *   - frontend/src/features/mars-forecasts/DecompositionTab.tsx
 *     (CONTRIBUTOR_COLORS, FALLBACK_COLORS)
 *
 * Callers are NOT migrated yet -- this module is foundation only.
 *
 * Aliasing decision: the canonical key set is
 *   Equities | Bonds | Commodities | Inflation | Cash | Alternatives
 * The API also emits the display strings "Fixed Income" (=== "Bonds") and
 * "Inflation Swaps" (=== "Inflation"); callers that key off those display
 * names should normalise at the call site.
 */

/** Canonical asset-class identifiers used by chart code. */
export type AssetClass =
  | 'Equities'
  | 'Bonds'
  | 'Commodities'
  | 'Inflation'
  | 'Cash'
  | 'Alternatives';

/**
 * Canonical hex palette per asset class. Hex values match
 * mars-portfolio/chartUtils.ts and DecompositionTab.tsx.
 *
 * Cash / Alternatives: assigned neutral Fulcrum brand-adjacent hexes.
 * Update if/when the design system formalises them.
 */
export const ASSET_COLORS: Record<AssetClass, string> = {
  Equities: '#ec665c',
  Bonds: '#729dcd',
  Commodities: '#ff9d4a',
  Inflation: '#9467bd',
  Cash: '#314858',
  Alternatives: '#8dd3c7',
};

/**
 * Asset palette extended with the display-string aliases used by mars-portfolio:
 *   - "Fixed Income"     === Bonds
 *   - "Inflation Swaps"  === Inflation
 *   - "Portfolio"        === Cash hex (slate)
 *   - "Benchmark"        === Alternatives hex (mint)
 *
 * Prefer `ASSET_COLORS` (canonical keys); use this map only when the call
 * site receives display strings straight from the API.
 */
export const ASSET_COLORS_WITH_ALIASES: Record<string, string> = {
  ...ASSET_COLORS,
  'Fixed Income': ASSET_COLORS.Bonds,
  'Inflation Swaps': ASSET_COLORS.Inflation,
  Portfolio: ASSET_COLORS.Cash,
  Benchmark: ASSET_COLORS.Alternatives,
};

/** Fulcrum brand palette. */
export const FULCRUM_COLORS = {
  lightTeal: '#7ACAC5',
  peacockBlue: '#193947',
  coral: '#DD6E63',
} as const;

/**
 * Named contributor colours used by the Decomposition tab.
 * Mirrors `CONTRIBUTOR_COLORS` in
 * `features/mars-forecasts/DecompositionTab.tsx`.
 */
export const CONTRIBUTOR_COLORS: Record<string, string> = {
  'Real Risk-Free Rate': '#5569C9',
  'Stock Excess Return': '#DD6E63',
  'Bond Excess Return': '#1569CC',
  'Commodity Excess Return': '#F1A35B',
  'Swap Excess Return': '#93526B',
  'Price-Dividend Ratio': '#7ACAC5',
  'Dividend Growth': '#195546',
  'Term Spread': '#8D69B8',
  'Commodity Carry': '#BB9C70',
  'Commodity Price': '#2D3038',
  'Swap Rate': '#FFCE4C',
  'Credit Spread': '#7A9DCA',
  Inflation: '#93526B',
  'Real GDP': '#897871',
  'Inflation Cycle': '#BDD6EB',
  Other: '#4c9487',
};

/** Fallback rotation when a contributor name has no explicit mapping. */
export const CONTRIBUTOR_FALLBACK_COLORS: readonly string[] = [
  '#DD6E63', '#5569C9', '#8D69B8', '#F1A35B', '#7ACAC5',
  '#93526B', '#FFCE4C', '#7A9DCA', '#BB9C70', '#195546',
];

const HEX3 = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/;
const HEX6 = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/;

/**
 * Convert a CSS hex colour (`#rgb` or `#rrggbb`) to an
 * `rgba(r, g, b, a)` string. Throws on malformed input -- callers should
 * pass already-validated palette values.
 */
export function hexToRgba(hex: string, alpha: number): string {
  if (typeof hex !== 'string') {
    throw new TypeError(`hexToRgba: expected string, got ${typeof hex}`);
  }

  const short = HEX3.exec(hex);
  if (short) {
    const r = parseInt(short[1]! + short[1]!, 16);
    const g = parseInt(short[2]! + short[2]!, 16);
    const b = parseInt(short[3]! + short[3]!, 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const long = HEX6.exec(hex);
  if (long) {
    const r = parseInt(long[1]!, 16);
    const g = parseInt(long[2]!, 16);
    const b = parseInt(long[3]!, 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  throw new Error(`hexToRgba: invalid hex color "${hex}"`);
}
